import { UnexpectedError } from '@watershed/errors/UnexpectedError';

import { doubleQuote } from '@watershed/lscript/sql';
import {
  /* eslint-disable @watershed/no-dangerous-raw-sql -- DuckDB usage only */
  dangerousRawSql,
  Identifier,
  RawSQL,
  sqlF,
  sqlIdentifier,
  sqlJoinF,
} from '@watershed/lscript/sqlTemplate';
import { unitToString } from '@watershed/lscript/unit';
import { ASTCall } from '@watershed/lscript/ast';
import { ExprType } from '@watershed/lscript/expr_types';
import { TraceASTExpr } from '../trace';
import { Context, SqlError, SqlExpression, SqlTrace } from '../types';
import { findReference, Reference } from '../utils/gatherReferences';
import { ReferenceSql, referenceToSql } from './reference';

export const privateSqlState: unique symbol = Symbol('sqlState');

export type UsedReference = {
  sql: ReferenceSql;
  columns: Set<string>;
};

export type PrivateSqlState = {
  // Because of the way we compile execution stages (see note on SqlExpression)
  // we need to know which input variables we need
  inputVariables: Set<string>;
  // This map contains expressions that describe how to reference a variable
  // defined elsewhere in the program. We populate this map during the
  // program-to-SQL compilation process with the variables that are defined as
  // of a given moment in the execution
  variablesSql: Map<string, SqlExpression>;
  // This map contains the traces of all variables that have them. This is a bit
  // different than the trace on the matching entry in variablesSql: that value
  // might be annotated with the variable, while this one will just be the trace
  // for the trace of the right-hand-side of the assignment (plus any implicit
  // unit conversions, if any)
  traces: Map<string, SqlTrace>;

  references: Array<Reference>;
  usedReferences: Map<string, UsedReference>;
};

// We use this alias to refer to the list of variables the program knows about
// right now, including any input variables
export const PROGRAM_TABLE = '__p';
export const PROGRAM_TABLE_I = sqlIdentifier(PROGRAM_TABLE);

export function rowIdCol(s: string): string {
  // No need to prefix __ since this gets called with __input and __join columns
  return `${s}_row_id`;
}

export function rowIdColI(s: string): Identifier {
  return sqlIdentifier(rowIdCol(s));
}

export function debugParamsColI(s: string): Identifier {
  return sqlIdentifier(`${s}_debug_params`);
}

export function getState(ctx: Context): PrivateSqlState {
  if (!(ctx[privateSqlState] as PrivateSqlState | undefined)) {
    throw new Error(`No SQL state in context! Did you call withSqlState?`);
  }
  return ctx[privateSqlState];
}

export function addRef(
  ctx: Context,
  ref: ASTCall,
  cols: Array<string>
): ReferenceSql {
  const state = getState(ctx);
  const reference = findReference(state.references, ref);
  const used = addReference(ctx, reference);
  for (const col of cols) {
    used.columns.add(col);
  }
  return used.sql;
}

// Add a reference to the set of used references, if it hasn't been added
// already. This triggers the set of matching conditions to be evaluated /
// turned into SQL, so any variables referenced in those matching conditions
// must already exist in the context.
function addReference(ctx: Context, reference: Reference) {
  const state = getState(ctx);
  let used = state.usedReferences.get(reference.name);
  if (used) {
    return used;
  }
  const refSql = referenceToSql(ctx, reference);
  used = {
    sql: refSql,
    columns: new Set(),
  };
  state.usedReferences.set(reference.name, used);
  return used;
}

// Returns SQL expression for accessing the input variable
export function addInputVariable(ctx: Context, name: string): string {
  const state = getState(ctx);
  state.inputVariables.add(name);
  return doubleQuote(`${ctx.inputTable}.${name}`);
}

// Returns SQL expression for accessing an optional input variable.
// If the column does not exist on the input type, returns NULL instead
// of referencing a non-existent column. This allows callers to safely
// coalesce to a fallback value at the call site without failing binding.
export function maybeAddInputVariable(
  ctx: Context,
  name: string,
  duckDbType?: string
): string {
  if (!ctx.inputType.has(name)) {
    return duckDbType ? `CAST(NULL AS ${duckDbType})` : 'NULL';
  }
  return addInputVariable(ctx, name);
}

export function assertNoUnitExpr(sql: SqlExpression): SqlExpression {
  if (sql.unitExpr) {
    throw new UnexpectedError(
      `Quantity not allowed here, there must be a bug in LScript code`,
      { data: { sqlExpr: sql } }
    );
  }
  return sql;
}

export function mergeErrors(errors: Array<SqlError | null>): SqlError | null {
  const es = errors.filter((error): error is SqlError => error !== null);
  if (es.length === 0) {
    return null;
  } else if (es.length === 1) {
    return es[0];
  } else {
    const arms = es.map((e) => ` WHEN ${e.exists} THEN ${e.expr}`);
    return {
      exists: es.map((e) => e.exists).join(' OR '),
      expr: `CASE${arms.join('')} END`,
    };
  }
}

export type GenTraceSql<T extends TraceASTExpr> = { t: T['t'] } & {
  [Key in keyof Omit<T, 't' | 'v' | 'u'>]: T[Key] extends TraceASTExpr
    ? RawSQL
    : T[Key] | RawSQL | Identifier;
};

// This is a very helpful but slightly confusing function that helps generate
// LScript traces (see lscript/trace.ts for more) as part of SQL evaluation.
//
// Pass in:
// - ty: the type of the expression being returned
// - eSql: the non-trace SQL info. See the documentation on SqlExpression
// - cb: a callback where all of the interesting trace works happen. More on
//   this below
//
// A trace is comprised of two parts (see the SqlTrace type). As an
// implementation detail today, these two parts have similar values: the
// fingerprint is the ordinary expression with the numeric values removed
// (except for number literals that appear in the source; in that case we pass
// valueMattersInFingerprint: true), making it less data-specific. The two
// expressions are otherwise identical.
//
// In order to help maintain these two very similar representations, callers
// pass a callback (`cb`) that is called twice, once to calculate and return
// the full trace value, and once for the fingerprint. The callback is called
// with one parameter, `sub`, a function that, given a trace, returns either
// the main `expr` or the `fingerprintExpr` depending on which "mode" we're in
// (i.e., when generating the fingerprint expression, the `sub` function returns
// the fingerprint expression, and vice versa)
//
// The return value of this callback is either an arbitrary bit of SQL, or an
// object that looks a lot like a TraceASTExpr. If it's the latter, it will be
// converted into SQL that returns a JSON object.
//
// If you ever find yourself debugging the SQL statements produced by this
// function, here is a pretty-printer that formats `json_object(...)`-style
// queries into something more readable:
// https://gist.github.com/zenazn/aa8b48c5ca982b89b2e31027d1f41ec2
export function traceHelper<T extends TraceASTExpr>(
  ty: ExprType,
  eSql: Omit<SqlExpression, 'trace'>,
  cb: (sub: (t: SqlTrace | null) => RawSQL) => RawSQL | GenTraceSql<T>,
  opts?: {
    valueMattersInFingerprint: boolean;
  }
): SqlExpression {
  if (ty.ty === 'nullable') {
    ty = ty.baseTy;
  }

  let unit: string | RawSQL | null;
  if (ty.ty === 'number') {
    unit = ty.unit ? unitToString(ty.unit) : null;
  } else if (ty.ty === 'quantity') {
    UnexpectedError.invariant(eSql.unitExpr, 'quantities must have unit exprs');
    unit = dangerousRawSql(eSql.unitExpr);
  } else {
    return {
      expr: eSql.expr,
      stage: eSql.stage,
      unitExpr: eSql.unitExpr,
      error: eSql.error,
      trace: null,
    };
  }

  function genToSql(
    g: GenTraceSql<T>,
    v: RawSQL | undefined,
    u: string | RawSQL | null
  ): RawSQL {
    const arms: Array<RawSQL> = [];
    arms.push(sqlF`'t'`);
    arms.push(sqlF`${g.t}`);
    if (v) {
      arms.push(sqlF`'v'`);
      arms.push(sqlF`round(${v},8)`);
    }
    arms.push(sqlF`'u'`);
    arms.push(sqlF`${u}`);
    for (const [k, v] of Object.entries(g)) {
      if (k === 't') {
        continue;
      }
      arms.push(sqlF`${k}`);
      arms.push(sqlF`${v}`);
    }
    return sqlF`json_object(${sqlJoinF(arms, ',')})`;
  }

  const exprVal = cb((t) => t?.expr ?? sqlF`NULL`);
  const fingerprintValue = cb((t) => t?.fingerprintExpr ?? sqlF`NULL`);

  return {
    expr: eSql.expr,
    stage: eSql.stage,
    unitExpr: eSql.unitExpr,
    error: eSql.error,
    trace: {
      expr:
        exprVal instanceof RawSQL
          ? exprVal
          : genToSql(exprVal, dangerousRawSql(eSql.expr), unit),
      // Generally a fingerprint is equivalent to a trace AST, minus the actual
      // numerical value that was used. In some cases though, like a numeric literal
      // embeded in the program, the value is an important part of the trace
      fingerprintExpr:
        fingerprintValue instanceof RawSQL
          ? fingerprintValue
          : genToSql(
              fingerprintValue,
              opts?.valueMattersInFingerprint
                ? dangerousRawSql(eSql.expr)
                : undefined,
              unit
            ),
    },
  };
}
